/**
 * Vague 1 · Phase C.4 · Badge "statut réseau + actions en attente".
 *
 * Visible en haut de la TodayPage, à côté du logout. Sert d'indicateur
 * de confiance pour l'agent : "mes pointages partent en direct" vs
 * "mes pointages sont stockés sur le téléphone, ils partiront tout à
 * l'heure".
 *
 * Comportement :
 *  - Online + 0 op pending : rond vert discret, pas de label
 *  - Online + N op pending : rond orange + "N en attente" + click pour
 *    forcer un flush
 *  - Offline : rond rouge + "Hors réseau"
 *  - Failed ops : rond rouge + "N échec" (click → flush retry)
 */
import { useState } from "react";
import { flushOutbox } from "../../lib/sync-manager";
import { useNetworkStatus } from "../../lib/network";
import { useOutboxCount } from "../../lib/use-outbox";

export function NetworkBadge() {
  const { online } = useNetworkStatus();
  const { pending, failed } = useOutboxCount();
  const [busy, setBusy] = useState(false);

  const hasQueue = pending + failed > 0;

  const handleFlush = async () => {
    if (busy || !online) return;
    setBusy(true);
    try {
      await flushOutbox();
    } finally {
      setBusy(false);
    }
  };

  if (online && !hasQueue) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-green-700"
        title="En ligne, tout est synchronisé"
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
        En ligne
      </div>
    );
  }

  if (!online) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-red-700"
        title="Hors réseau · vos actions sont stockées localement"
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
        Hors réseau
        {hasQueue ? (
          <span className="ml-0.5 text-red-900">· {pending + failed}</span>
        ) : null}
      </div>
    );
  }

  // online + queue > 0
  const color = failed > 0 ? "red" : "amber";
  return (
    <button
      type="button"
      onClick={handleFlush}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
        color === "red"
          ? "bg-red-50 text-red-700 hover:bg-red-100"
          : "bg-amber-50 text-amber-700 hover:bg-amber-100"
      } disabled:opacity-60`}
      title="Cliquez pour synchroniser maintenant"
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          color === "red" ? "bg-red-500" : "bg-amber-500"
        } ${busy ? "animate-pulse" : ""}`}
      />
      {failed > 0 ? `${failed} échec` : `${pending} en attente`}
    </button>
  );
}
